import { useState, useEffect } from "react";
import { DashboardLayout } from "@layouts/DashboardLayout";
import {
  getUserNotifications,
  markReadNotification,
} from "@services/krent.service";
import { NotificationsInterfaceResponse } from "@interfaces/notifications.interface";
import { useSessionStorage } from "../../hooks";
import { useNavigate } from "react-router-dom";

export default function NotificationsPage() {
  const [user] = useSessionStorage("user", {});
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<
    NotificationsInterfaceResponse[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        const abortController = new AbortController();
        const response = await getUserNotifications(
          user.id,
          abortController.signal,
        );
        setNotifications(response.data || []);
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [user?.id]);

  const handleViewClick = async (notificationId: string, notification: any) => {
    try {
      const abortController = new AbortController();
      await markReadNotification(notificationId, abortController.signal);
      // Update the notification to marked as read locally
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)),
      );
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }

    // Route based on notification type
    if (notification.type === "wallet") {
      navigate("/caution-fee");
    } else if (notification.type === "property") {
      navigate("/listings");
    } else if (notification.type === "transaction") {
      if (user?.role === "user") {
        navigate("/my-bookings");
      } else {
        navigate("/requests");
      }
    } else {
      // Default fallback
      if (user?.role === "user") {
        navigate("/my-bookings");
      } else {
        navigate("/requests");
      }
    }
  };

  const formatTimestamp = (date: Date | string) => {
    const now = new Date();
    const timestamp = new Date(date);
    const diffMs = now.getTime() - timestamp.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return timestamp.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <DashboardLayout
        title="Notifications"
        subtitle="Manage your account notification on Krent"
      >
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "100vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Notifications"
      subtitle="Manage your account notification on Krent"
    >
      <div>
        <div className="container pt-4">
          <div
            className="card border-0"
            style={{
              borderRadius: 16,
              overflow: "hidden",
              background: "#FAFAFA",
              maxHeight: "600px",
              overflowY: "auto",
            }}
          >
            {notifications.length === 0 ? (
              <div className="text-center py-5 px-4">
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: "#f3f4f6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                    color: "#9ca3af",
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                </div>
                <p className="mb-0" style={{ color: "#6b7280", fontSize: 14 }}>
                  You're all caught up!
                </p>
              </div>
            ) : (
              notifications.map((notification, index) => {
                return (
                  <div
                    key={notification.id}
                    style={{
                      borderBottom:
                        index < notifications.length - 1
                          ? "1px solid #f3f4f6"
                          : "none",
                      padding: "22px 28px",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 16,
                      transition: "background 0.15s",
                      background: notification.read ? "#f9fafb" : "#fff",
                      cursor: "default",
                      borderLeft: notification.read
                        ? "none"
                        : "3px solid #14b8a6",
                    }}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLDivElement).style.background =
                        "#fafafa")
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLDivElement).style.background =
                        "#fff")
                    }
                  >
                    {/* Content */}
                    <div className="flex-grow-1 min-w-0">
                      <p
                        className="mb-1"
                        style={{
                          fontWeight: notification.read ? 500 : 600,
                          fontSize: 15,
                          color: notification.read ? "#6b7280" : "#111827",
                          lineHeight: 1.4,
                        }}
                      >
                        {notification.title}
                      </p>
                      <p
                        className="mb-2"
                        style={{
                          fontSize: 13.5,
                          color: "#6b7280",
                          lineHeight: 1.55,
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {notification.message}
                      </p>
                      <span
                        style={{
                          fontSize: 12,
                          color: "#9ca3af",
                          fontWeight: 500,
                        }}
                      >
                        {formatTimestamp(notification.createdAt)}
                      </span>
                    </div>

                    <div
                      className="d-flex align-items-center gap-2 flex-shrink-0"
                      style={{ marginTop: 2 }}
                    >
                      <button
                        onClick={() =>
                          handleViewClick(notification.id, notification)
                        }
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#14b8a6",
                          fontWeight: 600,
                          fontSize: 13.5,
                          textDecoration: "none",
                          whiteSpace: "nowrap",
                          padding: 0,
                          lineHeight: 1,
                        }}
                        onMouseEnter={(e) =>
                          ((e.currentTarget as HTMLButtonElement).style.color =
                            "#0d9488")
                        }
                        onMouseLeave={(e) =>
                          ((e.currentTarget as HTMLButtonElement).style.color =
                            "#14b8a6")
                        }
                      >
                        View here
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
